import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Checkout() {
  const navigate = useNavigate();
  const [listingData, setListingData] = useState<any>(null);
  const [selectedPackage, setSelectedPackage] = useState<any>(null);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardData, setCardData] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
  });
  const [billingAddress, setBillingAddress] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
  });

  useEffect(() => {
    const data = sessionStorage.getItem('listingData');
    const packageData = sessionStorage.getItem('selectedPackage');
    
    if (data && packageData) {
      setListingData(JSON.parse(data));
      setSelectedPackage(JSON.parse(packageData));
    } else {
      navigate('/pricing');
    }
  }, [navigate]);

  const handleCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    if (e.target.name === 'cardNumber') {
      value = value.replace(/\s/g, '').replace(/(\d{4})/g, '$1 ').trim();
      if (value.length > 19) return;
    }
    
    if (e.target.name === 'expiryDate') {
      value = value.replace(/\D/g, '');
      if (value.length >= 2) {
        value = value.slice(0, 2) + '/' + value.slice(2, 4);
      }
      if (value.length > 5) return;
    }
    
    if (e.target.name === 'cvv') {
      value = value.replace(/\D/g, '');
      if (value.length > 4) return;
    }
    
    setCardData({
      ...cardData,
      [e.target.name]: value,
    });
  };

  const handleBillingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBillingAddress({
      ...billingAddress,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      sessionStorage.removeItem('listingData');
      sessionStorage.removeItem('selectedPackage');
      // Show success message or redirect
      alert('Payment successful! Your listing will be reviewed and published within 24 hours.');
      navigate('/');
    }, 2000);
  };

  const getPackageName = (type: string) => {
    const names: Record<string, string> = {
      basic: 'Basic Listing',
      essential: 'Essential Listing',
      enhanced: 'Enhanced Listing',
      claim: 'Claim Your Listing',
    };
    return names[type] || type;
  };

  const calculateTotal = () => {
    if (!selectedPackage) return 0;
    const subtotal = selectedPackage.price;
    const processingFee = subtotal * 0.029;
    const tax = (subtotal + processingFee) * 0.08;
    return (subtotal + processingFee + tax).toFixed(2);
  };

  if (!listingData || !selectedPackage) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Complete Your Purchase</h1>
            <p className="text-xl text-gray-600">Secure checkout for your healthcare listing</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Payment Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Payment Method Selection */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Method</h2>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('card')}
                      className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                        paymentMethod === 'card'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <i className="ri-bank-card-line text-3xl text-gray-700 mb-2"></i>
                      <div className="text-sm font-medium text-gray-900">Card</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('paypal')}
                      className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                        paymentMethod === 'paypal'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <i className="ri-paypal-fill text-3xl text-blue-600 mb-2"></i>
                      <div className="text-sm font-medium text-gray-900">PayPal</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMethod('apple')}
                      className={`p-4 border-2 rounded-lg transition-all cursor-pointer ${
                        paymentMethod === 'apple'
                          ? 'border-teal-600 bg-teal-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <i className="ri-apple-fill text-3xl text-gray-900 mb-2"></i>
                      <div className="text-sm font-medium text-gray-900">Apple Pay</div>
                    </button>
                  </div>

                  {paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="cardNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                          Card Number *
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="cardNumber"
                            name="cardNumber"
                            value={cardData.cardNumber}
                            onChange={handleCardChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            placeholder="1234 5678 9012 3456"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex gap-2">
                            <i className="ri-visa-line text-2xl text-blue-600"></i>
                            <i className="ri-mastercard-line text-2xl text-orange-600"></i>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="cardName" className="block text-sm font-semibold text-gray-700 mb-2">
                          Cardholder Name *
                        </label>
                        <input
                          type="text"
                          id="cardName"
                          name="cardName"
                          value={cardData.cardName}
                          onChange={(e) => setCardData({ ...cardData, cardName: e.target.value })}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          placeholder="John Smith"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label htmlFor="expiryDate" className="block text-sm font-semibold text-gray-700 mb-2">
                            Expiry Date *
                          </label>
                          <input
                            type="text"
                            id="expiryDate"
                            name="expiryDate"
                            value={cardData.expiryDate}
                            onChange={handleCardChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            placeholder="MM/YY"
                          />
                        </div>
                        <div>
                          <label htmlFor="cvv" className="block text-sm font-semibold text-gray-700 mb-2">
                            CVV *
                          </label>
                          <input
                            type="text"
                            id="cvv"
                            name="cvv"
                            value={cardData.cvv}
                            onChange={handleCardChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            placeholder="123"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'paypal' && (
                    <div className="text-center py-8">
                      <i className="ri-paypal-fill text-6xl text-blue-600 mb-4"></i>
                      <p className="text-gray-600 mb-4">You will be redirected to PayPal to complete your payment</p>
                      <button
                        type="button"
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium cursor-pointer whitespace-nowrap"
                      >
                        Continue with PayPal
                      </button>
                    </div>
                  )}

                  {paymentMethod === 'apple' && (
                    <div className="text-center py-8">
                      <i className="ri-apple-fill text-6xl text-gray-900 mb-4"></i>
                      <p className="text-gray-600 mb-4">Use Apple Pay for a quick and secure checkout</p>
                      <button
                        type="button"
                        className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium cursor-pointer whitespace-nowrap"
                      >
                        Pay with Apple Pay
                      </button>
                    </div>
                  )}
                </div>

                {/* Billing Address */}
                {paymentMethod === 'card' && (
                  <div className="bg-white rounded-2xl shadow-lg p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Billing Address</h2>
                    
                    <div className="space-y-4">
                      <div>
                        <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                          Street Address *
                        </label>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          value={billingAddress.address}
                          onChange={handleBillingChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                          placeholder="123 Main Street"
                        />
                      </div>

                      <div className="grid md:grid-cols-3 gap-4">
                        <div>
                          <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                            City *
                          </label>
                          <input
                            type="text"
                            id="city"
                            name="city"
                            value={billingAddress.city}
                            onChange={handleBillingChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            placeholder="New York"
                          />
                        </div>
                        <div>
                          <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                            State *
                          </label>
                          <input
                            type="text"
                            id="state"
                            name="state"
                            value={billingAddress.state}
                            onChange={handleBillingChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            placeholder="NY"
                          />
                        </div>
                        <div>
                          <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
                            ZIP Code *
                          </label>
                          <input
                            type="text"
                            id="zipCode"
                            name="zipCode"
                            value={billingAddress.zipCode}
                            onChange={handleBillingChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                            placeholder="10001"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                {paymentMethod === 'card' && (
                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full px-8 py-4 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    {isProcessing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ri-lock-line mr-2"></i>
                        Complete Payment
                      </>
                    )}
                  </button>
                )}
              </form>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-hospital-line text-2xl text-teal-600"></i>
                    </div>
                    <div className="ml-4 flex-1">
                      <h3 className="font-semibold text-gray-900">{getPackageName(selectedPackage.packageType)}</h3>
                      <p className="text-sm text-gray-600">{listingData.providerName}</p>
                      <p className="text-sm text-gray-500">{listingData.specialty}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4 space-y-3">
                  <div className="flex justify-between text-gray-600">
                    <span>{selectedPackage.billingCycle === 'monthly' ? 'Monthly' : 'Yearly'} Subscription</span>
                    <span>${selectedPackage.price.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Processing Fee (2.9%)</span>
                    <span>${(selectedPackage.price * 0.029).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax (8%)</span>
                    <span>${((selectedPackage.price + selectedPackage.price * 0.029) * 0.08).toFixed(2)}</span>
                  </div>
                </div>

                <div className="border-t border-gray-200 mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-teal-600">${calculateTotal()}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">Billed {selectedPackage.billingCycle}</p>
                </div>

                <div className="mt-6 bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <i className="ri-shield-check-line text-teal-600 text-xl mt-0.5"></i>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Secure Payment</h4>
                      <p className="text-xs text-gray-600">
                        Your payment information is encrypted and secure. Cancel anytime.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <i className="ri-check-line text-teal-600 mr-2"></i>
                    <span>Featured in search results</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-teal-600 mr-2"></i>
                    <span>Unlimited reviews</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-teal-600 mr-2"></i>
                    <span>Analytics dashboard</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-check-line text-teal-600 mr-2"></i>
                    <span>Priority support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 flex justify-center items-center gap-8 flex-wrap">
            <div className="flex items-center text-gray-600">
              <i className="ri-lock-line text-2xl mr-2"></i>
              <span className="text-sm font-medium">SSL Encrypted</span>
            </div>
            <div className="flex items-center text-gray-600">
              <i className="ri-shield-check-line text-2xl mr-2"></i>
              <span className="text-sm font-medium">PCI Compliant</span>
            </div>
            <div className="flex items-center text-gray-600">
              <i className="ri-customer-service-line text-2xl mr-2"></i>
              <span className="text-sm font-medium">24/7 Support</span>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
